import React, { ReactNode, useContext, useMemo, useState } from 'react'

import { ChartType } from 'chart.js'
import { Trans } from '@lingui/macro'
import { sortBy } from 'lodash'

import { TimeUnitEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'

import ChartLine, {
  IFilters
} from '../../../../components/common/chart-line/ChartLine'
import IconCheck from '../../../../components/common/icon/IconCheck'
import IconEye from '../../../../components/common/icon/IconEye'
import IconGraph from '../../../../components/common/icon/IconGraph'
import IconPlane from '../../../../components/common/icon/IconPlane'
import Spinner from '../../../../components/common/spinner/Spinner'
import SurveysSummaryBoxes from './SurveySummaryBoxes'
import { motion } from 'framer-motion'

import { countWidthRate } from '../../helpers'
import { graphOptions } from './SurveySummaryChartOptions'

import { useSurveySummaryOpenQuery } from './SurveySummaryOpen.codegen'
import dayjs from 'shared/dayjs'
import { ManageSurveyContext } from '../ManageSurveyContext'

const SurveySummaryOpen = () => {
  const [chartTimeUnit, setChartTimeUnit] = useState<TimeUnitEnum>(
    TimeUnitEnum.DAY
  )
  const { survey } = useContext(ManageSurveyContext)
  const [responses, setResponses] = useState({
    totalCount: 0,
    startedCount: 0,
    completedCount: 0
  })

  const { totalCount, startedCount, completedCount } = responses

  const { data, loading } = useSurveySummaryOpenQuery({
    variables: {
      id: survey.id,
      chartTimeUnit
    }
  })

  useMemo(() => {
    if (data?.admin?.survey?.responses) {
      setResponses(data?.admin.survey.responses)
    }
  }, [data])

  const animationForOpen = {
    initial: {
      width: '0%'
    },
    animate: {
      width: `${countWidthRate(startedCount, totalCount)}`
    }
  }

  const animationForCompleted = {
    initial: {
      width: '0%'
    },
    animate: {
      width: `${countWidthRate(completedCount, totalCount)}`
    }
  }

  if (loading) {
    return <Spinner />
  }

  const surveyData = data?.admin?.survey
  let filter: IFilters | null = null
  let sortedDates: any[] = []
  let chartLine: ReactNode = null

  if (surveyData) {
    const publishedAt = dayjs(survey.published_at).subtract(1, 'days')

    const responseByDay = surveyData.surveyCompletedByTimeUnit
    const openedByDay = surveyData.surveyOpenedByTimeUnit
    const sendByDay = surveyData.surveySendByTimeUnit
    sortedDates = sortBy(
      [...responseByDay, ...openedByDay, ...sendByDay],
      'day'
    )

    if (sortedDates.length > 0) {
      filter = {
        from: dayjs(publishedAt).subtract(1, 'days').toString(),
        to: sortedDates[sortedDates.length - 1].day
      }

      chartLine = (
        <ChartLine
          width={800}
          height={200}
          config={{
            type: 'line' as ChartType,
            options: graphOptions
          }}
          data={[
            {
              dataset: responseByDay
            },
            {
              dataset: openedByDay,
              color: '11,146,213'
            },
            {
              dataset: sendByDay,
              color: '210,210,210'
            }
          ]}
          filter={filter}
          granularity={chartTimeUnit}
          onGranularityChanged={setChartTimeUnit}
        />
      )
    }
  }

  return (
    <>
      <div className="Summary">
        <div className="Summary__analytics">
          <div className="Summary__analytics__item --green">
            <IconCheck />
            <div className="Summary__analytics__container">
              <div className="Summary__analytics__number">{completedCount}</div>
              <div className="Summary__analytics__desc">
                <Trans>Reponses</Trans>
              </div>
            </div>
          </div>
          <div className="Summary__analytics__item --green">
            <IconGraph />
            <div className="Summary__analytics__container">
              <div className="Summary__analytics__number">
                {countWidthRate(completedCount, totalCount)}
              </div>
              <div className="Summary__analytics__desc">
                <Trans>Response rate</Trans>
              </div>
            </div>
          </div>
          <div className="Summary__analytics__item --blue">
            <IconEye />
            <div className="Summary__analytics__container">
              <div className="Summary__analytics__number">{startedCount}</div>
              <div className="Summary__analytics__desc">
                <Trans>Opened</Trans>
              </div>
            </div>
          </div>
          <div className="Summary__analytics__item --gray">
            <IconPlane />
            <div className="Summary__analytics__container">
              <div className="Summary__analytics__number">{totalCount}</div>
              <div className="Summary__analytics__desc">
                <Trans>Sent</Trans>
              </div>
            </div>
          </div>
        </div>

        {chartLine}

        <div className="Summary__stripe">
          <motion.span
            className="Summary__stripe__opened"
            {...animationForOpen}
          />
          <motion.span
            className="Summary__stripe__responses"
            {...animationForCompleted}
          />
        </div>

        <SurveysSummaryBoxes
          questions={survey.questions.length}
          recipients={totalCount}
          responses={completedCount}
        />
      </div>
    </>
  )
}

export default SurveySummaryOpen
